import { useEffect, useState } from "react";
import { usePaths } from "BEDROCK/paths";
import { useAnalyticsTracker } from "analytics-tracker";
import { StravaConnectButton } from "../components/strava-connect-button";
import { useStravaOAuth } from "../hooks/use-strava-oauth";
import { useStravaDestination } from "../hooks/use-strava-destination";
import { DEFAULT_COLLECTION_LIST_HANDLE } from "../constants";
import { ProjectPreview } from "components/common/project-preview";
import { useTemplateStore } from "entity-store/template-store/use-template-store";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { useTouchMode } from "BEDROCK/mobile";
import { StravaAnalyticsEvents } from "../types";
import styles from "./strava-landing-page.module.css";

/**
 * Landing page for the Strava acquisition funnel.
 * Introduces the integration and initiates the OAuth flow.
 * Optimized for both mobile and desktop layouts.
 */
const HERO_TEMPLATE_ID = "fb3ac5c8-fd77-4444-9c7d-8ecb8ccaa5da";
const DESTINATION_COLLECTION_ID = "9daeeac5-687b-4745-9651-ddaa1542ccf2";

// Achievement milestone examples to showcase different use cases
const MILESTONES = [
  {
    icon: "trophy",
    title: "First Marathon",
    description: "Commemorate your 26.2 mile achievement",
  },
  {
    icon: "bike",
    title: "Century Ride",
    description: "Mark your 100-mile cycling milestone",
  },
  {
    icon: "flag",
    title: "Personal Record",
    description: "Celebrate your fastest route ever",
  },
  {
    icon: "map",
    title: "Epic Adventure",
    description: "Capture that unforgettable vacation run",
  },
];

export const StravaLandingPage = () => {
  const paths = usePaths();
  const { trackEvent } = useAnalyticsTracker();
  const { fetchTemplate } = useTemplateStore();
  const { initiateOAuth } = useStravaOAuth();
  const { saveDestination } = useStravaDestination();
  const [heroTemplate, setHeroTemplate] = useState<ProjectTemplate | null>(
    null,
  );
  const isTouchMode = useTouchMode();

  // Track page view on mount and load hero template
  useEffect(() => {
    trackEvent(StravaAnalyticsEvents.LANDING_VIEWED);
    fetchTemplate(HERO_TEMPLATE_ID).then(setHeroTemplate);
  }, [trackEvent, fetchTemplate]);

  const handleOAuthInitiated = () => {
    saveDestination(DESTINATION_COLLECTION_ID, DEFAULT_COLLECTION_LIST_HANDLE);
    trackEvent(StravaAnalyticsEvents.OAUTH_INITIATED);
  };

  const renderMilestoneIcon = (iconName: string) => {
    switch (iconName) {
      case "trophy":
        return (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
            <path d="M4 22h16" />
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
          </svg>
        );
      case "bike":
        return (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="18.5" cy="17.5" r="3.5" />
            <circle cx="5.5" cy="17.5" r="3.5" />
            <circle cx="15" cy="5" r="1" />
            <path d="M12 17.5V14l-3-3 4-3 2 3h2" />
          </svg>
        );
      case "flag":
        return (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
        );
      case "map":
        return (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
            <line x1="9" y1="3" x2="9" y2="18" />
            <line x1="15" y1="6" x2="15" y2="21" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.page}>
      <main className={styles.main}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <h1 className={styles.headline}>
              Turn Your Strava Activities Into Stunning Wall Art
            </h1>
            <p className={styles.subheadline}>
              Connect your Strava account to see your route on a custom map
              poster in seconds. Premium prints, free UK delivery.
            </p>
            <div className={styles.ctaContainer}>
              <StravaConnectButton
                returnTo={paths.stravaCallback}
                onConnect={handleOAuthInitiated}
              />
            </div>
          </div>

          {/* Hero Image */}
          <div
            className={styles.heroImage}
            onClick={() => {
              handleOAuthInitiated();
              initiateOAuth(paths.stravaCallback);
            }}
          >
            {heroTemplate && (
              <ProjectPreview
                template={heroTemplate}
                size={
                  isTouchMode
                    ? { width: 320, height: 360 }
                    : { width: 420, height: 560 }
                }
                showOutline={false}
                showShadow={true}
                allowHoverEffect={true}
              />
            )}
          </div>
        </section>

        {/* Achievement Milestones */}
        <section className={styles.milestones}>
          <h2 className={styles.sectionTitle}>Commemorate Your Milestones</h2>
          <div className={styles.milestoneGrid}>
            {MILESTONES.map((milestone, index) => (
              <div key={index} className={styles.milestoneCard}>
                <div className={styles.milestoneIcon}>
                  {renderMilestoneIcon(milestone.icon)}
                </div>
                <h3 className={styles.milestoneTitle}>{milestone.title}</h3>
                <p className={styles.milestoneDesc}>{milestone.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className={styles.howItWorks}>
          <h2 className={styles.sectionTitle}>How It Works</h2>
          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                </svg>
              </div>
              <h3 className={styles.stepTitle}>Connect Strava</h3>
              <p className={styles.stepDesc}>
                Securely link your account with one tap
              </p>
            </div>

            <div className={styles.step}>
              <div className={styles.stepIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </div>
              <h3 className={styles.stepTitle}>Choose Your Style</h3>
              <p className={styles.stepDesc}>
                Pick a template, customize colors, fonts, and map style
              </p>
            </div>

            <div className={styles.step}>
              <div className={styles.stepIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="1" y="3" width="15" height="13" />
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
              </div>
              <h3 className={styles.stepTitle}>Free UK Delivery</h3>
              <p className={styles.stepDesc}>
                Premium, high qualityprints shipped in 4-7 working days
              </p>
            </div>
          </div>
        </section>

        {/* Secondary CTA */}
        <section className={styles.secondaryCta}>
          <h2 className={styles.ctaHeadline}>Ready to Create Yours?</h2>
          <StravaConnectButton
            returnTo={paths.stravaCallback}
            onConnect={handleOAuthInitiated}
          />
        </section>

        {/* Trust & Privacy */}
        <section className={styles.trust}>
          <h2 className={styles.trustTitle}>Your Data, Protected</h2>
          <div className={styles.trustGrid}>
            <div className={styles.trustItem}>
              <svg
                className={styles.trustIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <p className={styles.trustText}>Read-only access to activities</p>
            </div>
            <div className={styles.trustItem}>
              <svg
                className={styles.trustIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
              <p className={styles.trustText}>We never post to your feed</p>
            </div>
            <div className={styles.trustItem}>
              <svg
                className={styles.trustIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <p className={styles.trustText}>Disconnect anytime in Strava</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};
